import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, multiFactor, TotpMultiFactorGenerator } from '../../config/firebase';
import { MultiFactorInfo, TotpSecret } from 'firebase/auth';
import { logger } from '../../utils/logger';
import { QRCodeSVG } from 'qrcode.react';
import './MfaEnrollment.css';

type EnrollmentStep = 'intro' | 'qr' | 'verify' | 'success' | 'unenroll-confirm' | 'unenroll-success';

interface MfaEnrollmentProps {
  open: boolean;
  onClose: () => void;
  /** If true, user MUST enroll (cannot dismiss) */
  forced?: boolean;
}

export const MfaEnrollment: React.FC<MfaEnrollmentProps> = ({
  open,
  onClose,
  forced = false,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [qrUrl, setQrUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);

  // Check current MFA enrollment status when modal opens
  useEffect(() => {
    if (open && auth.currentUser) {
      const factors = multiFactor(auth.currentUser).enrolledFactors;
      const hasTOTP = factors.some((f: MultiFactorInfo) => f.factorId === 'totp');
      setIsEnrolled(hasTOTP);
      setStep(hasTOTP ? 'intro' : 'intro');
      setError('');
      setOtpCode('');
    }
  }, [open]);

  if (!open) return null;

  const handleStartEnrollment = async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const session = await multiFactor(currentUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);

      const url = secret.generateQrCodeUrl(
        currentUser.email || 'user',
        'FX+'
      );

      setTotpSecret(secret);
      setQrUrl(url);
      setSecretKey(secret.secretKey);
      setStep('qr');
    } catch (err: any) {
      logger.error('[MfaEnrollment] Error generating TOTP secret:', err);
      setError(t('mfa.enrollError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (!otpCode || otpCode.length !== 6 || !totpSecret) return;

    setLoading(true);
    setError('');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, otpCode);
      await multiFactor(currentUser).enroll(assertion, 'TOTP');

      setIsEnrolled(true);
      setStep('success');
    } catch (err: any) {
      logger.error('[MfaEnrollment] Enrollment verification error:', err);
      if (err?.code === 'auth/invalid-verification-code') {
        setError(t('mfa.invalidCode'));
      } else {
        setError(t('mfa.enrollError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const factors = multiFactor(currentUser).enrolledFactors;
      const totpFactor = factors.find((f: MultiFactorInfo) => f.factorId === 'totp');
      if (!totpFactor) throw new Error('No TOTP factor found');

      await multiFactor(currentUser).unenroll(totpFactor);

      setIsEnrolled(false);
      setStep('unenroll-success');
    } catch (err: any) {
      logger.error('[MfaEnrollment] Unenrollment error:', err);
      setError(t('mfa.enrollError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (forced && !isEnrolled) return; // Cannot dismiss if forced enrollment
    setStep('intro');
    setTotpSecret(null);
    setQrUrl('');
    setSecretKey('');
    setOtpCode('');
    setError('');
    onClose();
  };

  const renderIntro = () => (
    <>
      <div className="mfa-modal-header">
        <div className="mfa-modal-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            <circle cx="12" cy="16" r="1" />
          </svg>
        </div>
        <h2>{t('mfa.title')}</h2>
        {!forced && (
          <button className="mfa-close-button" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        )}
      </div>

      <div className="mfa-modal-content">
        {isEnrolled ? (
          <>
            <div className="mfa-status mfa-status-enabled">
              <span className="mfa-status-icon">&#10003;</span>
              <span>{t('mfa.enabled')}</span>
            </div>
            <p className="mfa-description">
              {t('mfa.enabledDescription')}
            </p>
          </>
        ) : (
          <>
            {forced && (
              <div className="mfa-forced-notice">
                <p>{t('mfa.forcedNotice')}</p>
              </div>
            )}
            <p className="mfa-description">
              {t('mfa.description')}
            </p>
            <div className="mfa-steps-preview">
              <div className="mfa-preview-step">
                <span className="mfa-preview-number">1</span>
                <span>{t('mfa.step1')}</span>
              </div>
              <div className="mfa-preview-step">
                <span className="mfa-preview-number">2</span>
                <span>{t('mfa.step2')}</span>
              </div>
              <div className="mfa-preview-step">
                <span className="mfa-preview-number">3</span>
                <span>{t('mfa.step3')}</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mfa-modal-actions">
        {isEnrolled ? (
          <>
            <button className="mfa-button mfa-button-secondary" onClick={handleClose}>
              {t('mfa.close')}
            </button>
            <button
              className="mfa-button mfa-button-danger"
              onClick={() => setStep('unenroll-confirm')}
            >
              {t('mfa.disable')}
            </button>
          </>
        ) : (
          <>
            {!forced && (
              <button className="mfa-button mfa-button-secondary" onClick={handleClose}>
                {t('mfa.cancel')}
              </button>
            )}
            <button
              className="mfa-button mfa-button-primary"
              onClick={handleStartEnrollment}
              disabled={loading}
            >
              {loading ? t('mfa.loading') : t('mfa.enable')}
            </button>
          </>
        )}
      </div>
    </>
  );

  const renderQrStep = () => (
    <>
      <div className="mfa-modal-header">
        <h2>{t('mfa.scanQrCode')}</h2>
      </div>

      <div className="mfa-modal-content">
        <p className="mfa-description">{t('mfa.scanInstructions')}</p>

        <div className="mfa-qr-container">
          {qrUrl && (
            <QRCodeSVG
              value={qrUrl}
              size={200}
              level="M"
              bgColor="#ffffff"
              fgColor="#000000"
            />
          )}
        </div>

        <div className="mfa-manual-entry">
          <p className="mfa-manual-label">{t('mfa.manualEntry')}</p>
          <code className="mfa-secret-key">{secretKey}</code>
        </div>

        {error && <div className="mfa-error">{error}</div>}
      </div>

      <div className="mfa-modal-actions">
        <button
          className="mfa-button mfa-button-secondary"
          onClick={() => { setStep('intro'); setError(''); }}
        >
          {t('mfa.back')}
        </button>
        <button
          className="mfa-button mfa-button-primary"
          onClick={() => { setStep('verify'); setError(''); }}
        >
          {t('mfa.next')}
        </button>
      </div>
    </>
  );

  const renderVerifyStep = () => (
    <>
      <div className="mfa-modal-header">
        <h2>{t('mfa.verifyCode')}</h2>
      </div>

      <div className="mfa-modal-content">
        <p className="mfa-description">{t('mfa.enterCodeInstructions')}</p>

        <form
          className="mfa-code-form"
          onSubmit={(e) => { e.preventDefault(); handleVerifyAndEnroll(); }}
        >
          <input
            type="text"
            className="mfa-code-input"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
          />
        </form>

        {error && <div className="mfa-error">{error}</div>}
      </div>

      <div className="mfa-modal-actions">
        <button
          className="mfa-button mfa-button-secondary"
          onClick={() => { setStep('qr'); setError(''); setOtpCode(''); }}
          disabled={loading}
        >
          {t('mfa.back')}
        </button>
        <button
          className="mfa-button mfa-button-primary"
          onClick={handleVerifyAndEnroll}
          disabled={loading || otpCode.length !== 6}
        >
          {loading ? t('mfa.verifying') : t('mfa.verify')}
        </button>
      </div>
    </>
  );

  const renderSuccess = () => (
    <>
      <div className="mfa-modal-header">
        <div className="mfa-modal-icon mfa-icon-success">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2>{t('mfa.enrollSuccess')}</h2>
      </div>

      <div className="mfa-modal-content">
        <p className="mfa-description">{t('mfa.enrollSuccessDescription')}</p>
      </div>

      <div className="mfa-modal-actions">
        <button className="mfa-button mfa-button-primary" onClick={handleClose}>
          {t('mfa.done')}
        </button>
      </div>
    </>
  );

  const renderUnenrollConfirm = () => (
    <>
      <div className="mfa-modal-header">
        <div className="mfa-modal-icon mfa-icon-warning">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2>{t('mfa.unenrollConfirm')}</h2>
      </div>

      <div className="mfa-modal-content">
        <p className="mfa-description">{t('mfa.unenrollWarning')}</p>
      </div>

      <div className="mfa-modal-actions">
        <button
          className="mfa-button mfa-button-secondary"
          onClick={() => { setStep('intro'); setError(''); }}
          disabled={loading}
        >
          {t('mfa.cancel')}
        </button>
        <button
          className="mfa-button mfa-button-danger"
          onClick={handleUnenroll}
          disabled={loading}
        >
          {loading ? t('mfa.loading') : t('mfa.confirmDisable')}
        </button>
      </div>

      {error && <div className="mfa-error" style={{ marginTop: '12px', textAlign: 'center' }}>{error}</div>}
    </>
  );

  const renderUnenrollSuccess = () => (
    <>
      <div className="mfa-modal-header">
        <h2>{t('mfa.unenrollSuccess')}</h2>
      </div>

      <div className="mfa-modal-content">
        <p className="mfa-description">{t('mfa.unenrollSuccessDescription')}</p>
      </div>

      <div className="mfa-modal-actions">
        <button className="mfa-button mfa-button-primary" onClick={handleClose}>
          {t('mfa.done')}
        </button>
      </div>
    </>
  );

  const renderStep = () => {
    switch (step) {
      case 'intro': return renderIntro();
      case 'qr': return renderQrStep();
      case 'verify': return renderVerifyStep();
      case 'success': return renderSuccess();
      case 'unenroll-confirm': return renderUnenrollConfirm();
      case 'unenroll-success': return renderUnenrollSuccess();
    }
  };

  return (
    <div className="mfa-overlay" onClick={forced ? undefined : (e) => e.target === e.currentTarget && handleClose()}>
      <div className="mfa-modal">
        {renderStep()}
      </div>
    </div>
  );
};
